<template>
  <transition name="fade-out" appear>
    <div v-if="show" class="fixed inset-0 bg-gradient-to-br from-slate-950 via-slate-900/95 to-[#020617] flex items-center justify-center z-50">
      <!-- 背景光效 - 减少模糊，保持清晰 -->
      <div class="absolute top-0 left-0 w-[720px] h-[420px] bg-gradient-to-br from-indigo-500/20 via-sky-500/10 to-transparent rounded-[100%] blur-[60px] pointer-events-none mix-blend-screen opacity-70 animate-pulse"></div>
      <div class="absolute bottom-0 right-0 w-[640px] h-[520px] bg-gradient-to-tl from-purple-500/20 via-indigo-500/10 to-transparent rounded-full blur-[50px] pointer-events-none mix-blend-screen opacity-60 animate-pulse"></div>
      
      <!-- 内容 -->
      <div class="relative z-10 text-center content-fade-in">
        <!-- 图标动画 -->
        <div class="mb-8 flex justify-center">
          <div class="relative">
            <!-- 外层呼吸光晕 -->
            <div class="absolute inset-0 bg-blue-400/20 rounded-full blur-2xl animate-ping"></div>
            <!-- 中层呼吸光晕 -->
            <div class="absolute inset-0 bg-blue-400/30 rounded-full blur-xl gear-outer-glow"></div>
            <!-- 内层容器 -->
            <div class="relative inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-gradient-to-br from-blue-500/30 to-indigo-500/20 backdrop-blur-sm border border-blue-500/30 shadow-2xl gear-container-glow">
              <i class="ph-bold ph-gear-six text-blue-400 text-5xl animate-spin-slow gear-breathing-glow"></i>
            </div>
          </div>
        </div>
        
        <!-- 标题动画 -->
        <h1 class="text-5xl font-bold text-white mb-4 animate-fade-in-up">
          运维管理平台
        </h1>
        <p class="text-xl text-slate-400 animate-fade-in-up-delay">
          系统运维与监控管理
        </p>
        
        <!-- 加载指示器 -->
        <div class="mt-12 flex justify-center gap-2">
          <div class="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style="animation-delay: 0s;"></div>
          <div class="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style="animation-delay: 0.2s;"></div>
          <div class="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style="animation-delay: 0.4s;"></div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
defineProps({
  show: {
    type: Boolean,
    default: true
  }
});
</script>

<style scoped>
@keyframes spin-slow {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin-slow {
  animation: spin-slow 3s linear infinite;
}

/* 齿轮呼吸灯发光效果 - 柔和版 */
@keyframes breathing-glow {
  0%, 100% {
    filter: drop-shadow(0 0 6px rgba(96, 165, 250, 0.4)) 
            drop-shadow(0 0 12px rgba(96, 165, 250, 0.3));
    opacity: 1;
  }
  50% {
    filter: drop-shadow(0 0 10px rgba(96, 165, 250, 0.6)) 
            drop-shadow(0 0 20px rgba(96, 165, 250, 0.4))
            drop-shadow(0 0 30px rgba(96, 165, 250, 0.2));
    opacity: 1;
  }
}

/* 外层光晕呼吸效果 */
@keyframes outer-glow {
  0%, 100% {
    opacity: 0.2;
    transform: scale(1);
  }
  50% {
    opacity: 0.35;
    transform: scale(1.05);
  }
}

/* 容器呼吸光晕 */
@keyframes container-glow {
  0%, 100% {
    box-shadow: 0 0 15px rgba(96, 165, 250, 0.2),
                0 0 30px rgba(96, 165, 250, 0.15);
  }
  50% {
    box-shadow: 0 0 25px rgba(96, 165, 250, 0.35),
                0 0 50px rgba(96, 165, 250, 0.25),
                0 0 75px rgba(96, 165, 250, 0.15);
  }
}

.gear-breathing-glow {
  animation: spin-slow 3s linear infinite, breathing-glow 2s ease-in-out infinite;
}

.gear-outer-glow {
  animation: outer-glow 2.5s ease-in-out infinite;
}

.gear-container-glow {
  animation: container-glow 2.5s ease-in-out infinite;
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fade-in-up 0.8s ease-out;
}

.animate-fade-in-up-delay {
  animation: fade-in-up 0.8s ease-out 0.3s both;
}

/* 进入动画 - 3秒 */
.fade-out-enter-active {
  transition: opacity 3s ease-out, transform 3s ease-out;
}

.fade-out-enter-from {
  opacity: 0;
  transform: scale(0.95);
}

.fade-out-enter-to {
  opacity: 1;
  transform: scale(1);
}

/* 离开动画 - 淡出并缩放 */
.fade-out-leave-active {
  transition: opacity 0.8s ease-in, transform 0.8s ease-in;
}

.fade-out-leave-from {
  opacity: 1;
  transform: scale(1);
}

.fade-out-leave-to {
  opacity: 0;
  transform: scale(1.05);
}

/* 内容区域淡入动画 */
@keyframes content-fade-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.content-fade-in {
  animation: content-fade-in 0.6s ease-out forwards;
}
</style>

